<template>
  <div>
    <Dialog
        :header="t('ADD_NEW_DOCUMENT')"
        v-model:visible="newDocumentVisible"
        modal
        class="new-document-modal"
        :dismissable-mask="true"
        :draggable="false"
        @hide="onHideNewDocument"
    >
      <div class="mb-2">
        <SingleFileUpload
            :invalid="validationPending && !newDocumentFile"
            @select="onNewDocumentFileSelect"
            @remove="onNewDocumentFileClear"
        />
      </div>

      <div class="mb-2">
        <Textarea
            v-model="newDocumentDescription"
            :placeholder="t('CONTENT_DESCRIPTION')"
            :invalid="validationPending && !documentDescriptionValid"
            rows="5"
            class="w-100"
        />
      </div>

      <template #footer>
        <Button :label="t('CANCEL')" severity="secondary" @click="newDocumentVisible = false"/>
        <Button :label="t('SAVE')" :disabled="fileUploadPending" @click="addTrainingGroupDocument"/>
      </template>
    </Dialog>

    <div class="d-flex justify-content-end" v-if="auth.hasSomeRole(Role.ADMIN, Role.SCHOOL_MANAGER)">
      <Button :label="t('ADD_NEW_DOCUMENT')" @click="openNewTrainingGroupDocumentModal"/>
    </div>

    <DataTable
        :value="documents"
        :striped-rows="true"
        :removableSort="true"
        :rowHover="true"
        size="small"
        data-key="id"
    >
      <Column field="fileName" :header="t('FILE')" sortable/>
      <Column field="contentDescription" :header="t('CONTENT_DESCRIPTION')" sortable>
        <template #body="{ data }">
          <div class="ws-break-spaces">{{ data.contentDescription || "-" }}</div>
        </template>
      </Column>
      <Column field="createdBy" :header="t('AUTHOR')" sortable/>
      <Column field="createdAt" :header="t('TIME_ADDED')" sortable>
        <template #body="{ data }">
          {{ dateFormat(data.createdAt) }}&nbsp;{{ timeFormat(data.createdAt) }}
        </template>
      </Column>
      <Column>
        <template #body="{ data }">
          <Button
              v-if="auth.hasRole(Role.ADMIN)"
              v-tooltip="t('REMOVE')"
              icon="pi pi-trash"
              severity="danger"
              text
              rounded
              class="me-2"
              @click="showDeleteDocumentConfirmation(data.id)"
          />
          <Button
              v-tooltip="t('DOWNLOAD')"
              icon="pi pi-download"
              text
              rounded
              @click="downloadDocument(data.fileName, data.id)"
          />
        </template>
      </Column>
      <template #empty>
        {{ t('NO_RESULTS') }}
      </template>
    </DataTable>

    <file-downloader ref="fileDownloaderRef"/>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {Role} from "@/auth/oauth";
import type {TrainingGroupDocumentResponse} from "@/model/group";
import {useI18n} from "vue-i18n";
import {useDateFormat} from "@/mixins/dateFormatMixin2";
import useTimeFormat from "@/mixins/timeFormatMixin2";
import {useToast} from "@/util/toastUtils";
import eventBus from "@/util/eventBus";
import FileDownloader from "@/components/FileDownloader.vue";
import SingleFileUpload from "@/components/SingleFileUpload.vue";

const auth = injectAuth();
const axios = injectAxios();
const {t} = useI18n();
const toast = useToast();
const {dateFormat} = useDateFormat();
const {timeFormat} = useTimeFormat();

const props = defineProps<{
  groupId: number;
}>();

const documentDescriptionValid = computed(() => {
  return (newDocumentDescription.value?.length || 0) <= 512;
});

const newDocumentFormValid = computed(() => {
  return !!newDocumentFile.value && documentDescriptionValid.value;
});

const documents = ref<TrainingGroupDocumentResponse[]>([]);
const newDocumentFile = ref<File | null>(null);
const newDocumentDescription = ref("");
const validationPending = ref(false);
const fileUploadPending = ref(false);
const fileDownloaderRef = ref<InstanceType<typeof FileDownloader> | null>(null);
const newDocumentVisible = ref(false);

onMounted(async () => {
  await getTrainingGroupDocuments();
});

const onNewDocumentFileSelect = (e: { files: File[] }) => {
  newDocumentFile.value = e.files?.[0] ?? null;
};

const onNewDocumentFileClear = () => {
  newDocumentFile.value = null;
};

const onHideNewDocument = () => {
  newDocumentVisible.value = false;
};

const getTrainingGroupDocuments = async () => {
  const resp = await axios.get(`/training/group/${props.groupId}/documents`);
  documents.value = resp.data;
};

const openNewTrainingGroupDocumentModal = () => {
  validationPending.value = false;
  newDocumentFile.value = null;
  newDocumentDescription.value = "";
  newDocumentVisible.value = true;
};

const addTrainingGroupDocument = async (ev: Event) => {
  ev.preventDefault();

  if (!newDocumentFormValid.value) {
    validationPending.value = true;
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  try {
    validationPending.value = false;
    fileUploadPending.value = true;

    const params = {
      headers: {
        "Content-Type": "multipart/form-data",
        "Accept": "*/*",
      },
    };

    const requestData = {
      fileName: newDocumentFile.value?.name,
      contentDescription: newDocumentDescription.value,
    };

    const formData = new FormData();
    formData.append("file", newDocumentFile.value!);
    formData.append("request", new Blob([JSON.stringify(requestData)], {type: "application/json"}));

    await axios.post(`/training/group/${props.groupId}/documents`, formData, params);
    await getTrainingGroupDocuments();
    toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
  } finally {
    fileUploadPending.value = false;
    newDocumentFile.value = null;
    newDocumentDescription.value = "";
    newDocumentVisible.value = false;
  }
};

const deleteTrainingGroupDocument = async (id: number) => {
  await axios.delete(`/training/group/documents/${id}`);
  await getTrainingGroupDocuments();
  toast.makeSuccessToast(t("REMOVED"));
};

const downloadDocument = async (fileName: string, id: string) => {
  await fileDownloaderRef.value?.downloadFile(`/training/group/documents/${id}`, {}, fileName);
};

const showDeleteDocumentConfirmation = (id: number) => {
  eventBus.$emit("show-confirmation-modal", {
    callback: () => deleteTrainingGroupDocument(id),
  });
};
</script>
